import { AlertTriangle, Link2, Unlink, Video, Zap } from 'lucide-react';
import { formatCoupledAt, getCouplingComplianceAlerts } from './fleetCouplingData';

const workspaceSelectClass =
  'w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm text-black outline-none transition focus:border-cyan-500/50 focus:ring-1 focus:ring-logi-accent/30';

export default function VirtualCouplingStudio({
  movers,
  attachments,
  activeCouplings,
  selectedMoverId,
  selectedAttachmentId,
  onSelectMover,
  onSelectAttachment,
  onCouple,
  onDetach,
  onOpenDashcam,
}) {
  const availableMovers = movers.filter((m) => m.status === 'Available');
  const availableAttachments = attachments.filter((a) => a.status === 'Available');

  const getMover = (id) => movers.find((m) => m.id === id);
  const getAttachment = (id) => attachments.find((a) => a.id === id);

  return (
    <div className="space-y-5">
      <div className="rounded-xl border border-logi-accent/25 bg-gradient-to-br from-red-50 via-white to-red-50/80 p-5 ring-1 ring-logi-accent/20">
        <div className="mb-4 flex items-center gap-2">
          <Link2 className="h-5 w-5 text-logi-accent" />
          <div>
            <h3 className="text-sm font-bold text-black">Virtual Coupling Workspace</h3>
            <p className="text-[10px] text-black/70">
              Pair available mover heads with trailer / tanker units
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <div>
            <label className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-black">
              Select Available Mover
            </label>
            <select
              className={workspaceSelectClass}
              value={selectedMoverId}
              onChange={(e) => onSelectMover(e.target.value)}
            >
              <option value="">Choose mover head…</option>
              {availableMovers.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.plateNumber} · {m.id}
                </option>
              ))}
            </select>
            {availableMovers.length === 0 && (
              <p className="mt-1 text-[10px] font-semibold text-black/80">No movers currently available</p>
            )}
          </div>
          <div>
            <label className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-black">
              Select Available Attachment
            </label>
            <select
              className={workspaceSelectClass}
              value={selectedAttachmentId}
              onChange={(e) => onSelectAttachment(e.target.value)}
            >
              <option value="">Choose trailer / tanker…</option>
              {availableAttachments.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.serialRegistrationNo} · {a.type}
                </option>
              ))}
            </select>
            {availableAttachments.length === 0 && (
              <p className="mt-1 text-[10px] font-semibold text-black/80">
                No attachments currently available
              </p>
            )}
          </div>
        </div>

        <button
          type="button"
          onClick={onCouple}
          disabled={!selectedMoverId || !selectedAttachmentId}
          className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl border border-logi-accent/30 bg-cyan-100 px-5 py-3 text-sm font-bold text-black shadow-sm transition hover:bg-cyan-200 disabled:cursor-not-allowed disabled:opacity-40 sm:w-auto"
        >
          <Zap className="h-4 w-4" />
          Secure Coupling Hook
        </button>
      </div>

      <div>
        <h4 className="mb-3 text-xs font-bold uppercase tracking-wider text-logi-muted">
          Active Coupled Fleet ({activeCouplings.length})
        </h4>

        {activeCouplings.length === 0 ? (
          <p className="rounded-xl border border-dashed border-logi-grid py-10 text-center text-sm text-logi-text0">
            No active couplings. Use the workspace above to hook a mover to an attachment.
          </p>
        ) : (
          <div className="overflow-hidden rounded-xl border border-logi-grid bg-white">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[960px] text-left text-sm">
                <thead>
                  <tr className="border-b border-logi-grid/80 bg-white text-[10px] uppercase tracking-wider text-logi-text0">
                    <th className="px-3 py-2.5 font-semibold">Coupling ID</th>
                    <th className="px-3 py-2.5 font-semibold">Mover Head</th>
                    <th className="px-3 py-2.5 font-semibold">Attachment</th>
                    <th className="px-3 py-2.5 font-semibold">Route / Status</th>
                    <th className="px-3 py-2.5 font-semibold">Coupled Since</th>
                    <th className="px-3 py-2.5 font-semibold">Compliance</th>
                    <th className="px-3 py-2.5 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-logi-grid/30">
                  {activeCouplings.map((c) => {
                    const mover = getMover(c.moverId);
                    const attachment = getAttachment(c.attachmentId);
                    const alerts = getCouplingComplianceAlerts(mover, attachment);

                    return (
                      <tr key={c.id} className="hover:bg-logi-silver">
                        <td className="px-3 py-2.5 font-mono text-xs font-bold text-logi-accent">{c.id}</td>
                        <td className="px-3 py-2.5">
                          <p className="font-semibold text-logi-text">{mover?.plateNumber || '—'}</p>
                          <p className="text-[10px] text-logi-text0">{mover?.id}</p>
                        </td>
                        <td className="px-3 py-2.5">
                          <p className="font-semibold text-violet-300">{attachment?.serialRegistrationNo || '—'}</p>
                          <p className="text-[10px] text-logi-text0">{attachment?.type}</p>
                        </td>
                        <td className="px-3 py-2.5">
                          {c.routeStatus ? (
                            <div>
                              <span className="inline-flex rounded-full bg-logi-accent/10 px-2 py-0.5 text-[9px] font-bold uppercase text-logi-accent ring-1 ring-logi-accent/30">
                                {c.routeStatus}
                              </span>
                              {c.routeLabel && (
                                <p className="mt-1 max-w-[200px] text-[10px] leading-snug text-logi-muted">
                                  {c.routeLabel}
                                </p>
                              )}
                            </div>
                          ) : (
                            <span className="text-[10px] text-logi-text0">Yard standby</span>
                          )}
                        </td>
                        <td className="px-3 py-2.5 text-xs text-logi-muted">{formatCoupledAt(c.coupledAt)}</td>
                        <td className="px-3 py-2.5">
                          {alerts.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {alerts.map((a) => (
                                <span
                                  key={a.text}
                                  className={`inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-[9px] font-bold ring-1 ${
                                    a.severity === 'critical'
                                      ? 'animate-pulse bg-red-50 text-red-700 ring-red-500/25'
                                      : 'bg-amber-50 text-amber-700 ring-amber-500/25'
                                  }`}
                                >
                                  <AlertTriangle className="h-2.5 w-2.5" />
                                  {a.text}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="text-[10px] text-emerald-700">All clear</span>
                          )}
                        </td>
                        <td className="px-3 py-2.5">
                          <div className="flex flex-wrap gap-1.5">
                            <button
                              type="button"
                              onClick={() => onOpenDashcam(c)}
                              className="inline-flex items-center gap-1 rounded-lg border border-logi-accent/30 bg-logi-accent/10 px-2.5 py-1.5 text-[10px] font-bold text-logi-accent hover:bg-logi-accent/10"
                            >
                              <Video className="h-3.5 w-3.5" />
                              Live Dashcam Feed
                            </button>
                            <button
                              type="button"
                              onClick={() => onDetach(c.id)}
                              className="inline-flex items-center gap-1 rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-[10px] font-bold text-amber-700 hover:bg-amber-500/20"
                            >
                              <Unlink className="h-3.5 w-3.5" />
                              Detach / Force Swap
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
